import { useEffect, useMemo, useRef, useState } from 'react'
import { Ticket as TicketIcon, Search, Loader2, X, Plus, MessageSquare } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { nameInitial } from '../../lib/text.js'
import { useTypeahead } from '../../lib/useTypeahead.js'
import { asList } from '../../lib/datetime.js'
import {
  tId, tDetails, tTags, tBranchId, tPatientId, tPatientName, tHN, tPatientStatus,
  tFollowUps, tCreatedAt, tDateFollowup, tAgeDays, tFollowupRel, REL_TONES,
  fmtDuration, addDays, normalizeTagCatalog,
} from '../../lib/ticket.js'
import { STATUS_TONES, statusTone } from '../../data/patientStatuses.js'
import TicketDrawer from '../../components/ticket/TicketDrawer.jsx'
import TagPicker from '../../components/ticket/TagPicker.jsx'
import { MIN_PATIENT_SEARCH } from '../../lib/patientSearch.js'
import {
  Page, PageHeader, Button, Segmented, FilterCard, useFilters, Field, Input, FIELD_CLASS,
  DateRangePicker, BranchSelect, useBranches, branchFilterIds, branchName as kitBranchName,
  DataTable, Badge, PatientName, fmtDate, fmtNumber, bkkTodayISO,
} from '../../kit'

/* -------------------------------------------------------------------------- */
/* Ticket — the ติดตาม (follow-up) task/issue tracker, sibling of Recall. Each   */
/* row is a job/issue to work through; the status segmented picks done/not-done  */
/* and the filters narrow by date, branch, tag and patient.                      */
/*                                                                               */
/* Backed by GET /api/ticket → Xano /ticket (api:Bh0CIPqU). ⚠️ The row shape +    */
/* write contracts are NOT yet verified (Phase 0 — docs/ticket-endpoint-notes.md).*/
/* Field access goes through the tolerant t* accessors in lib/ticket.js so the UI */
/* survives whatever shape comes back. There is NO free-text search param on the  */
/* Xano side — patient lookups go through a picker → patient_id.                   */
/* -------------------------------------------------------------------------- */

// Verified live 2026-07-16 (docs/ticket-endpoint-notes.md):
//   · start_date/end_date window on `date_followup` — NOT created_at.
//   · end_date is EXCLUSIVE, so "up to and including D" must send D+1.
//   · rows come back already sorted by date_followup ascending.
//
// The two views filter date differently, by design:
//   ยังไม่เสร็จ — ONE date: everything due up to and including it (the worklist).
//   เสร็จแล้ว   — a date range.
const STATUS = [
  { key: 'not_done', label: 'ยังไม่เสร็จ', mode: 'not_done' },
  { key: 'done', label: 'เสร็จแล้ว', mode: 'done' },
]

// Remembered per browser (kit useFilters). `range` is a date filter, so it resets
// each new Bangkok day. Never patient data in here — the patient pick is NOT stored.
const DEFAULTS = { status: 'not_done', branch: '', tags: [], range: { start: '', end: '' } }

// A follow-up date can sit in the future even on a done ticket, so the range
// allows future days (as the old native inputs did) but offers the past presets.
const DONE_PRESETS = ['today', 'last7', 'last30', 'thisMonth', 'lastMonth']

export default function Ticket() {
  const { filters, set, reset, isDefault } = useFilters('ticket', DEFAULTS, { daily: ['range'] })
  // ยังไม่เสร็จ's single "ถึงวันที่". Deliberately NOT persisted: it defaults to today
  // on every open so the worklist can never go stale. '' means "show every unfinished".
  const [dueDate, setDueDate] = useState(bkkTodayISO())
  const [patient, setPatient] = useState(null) // { id, name, hn } — not persisted
  const [page, setPage] = useState(1)
  const [perPage] = useState(30)

  // /api/branches, once per session. Branch display + the drawer's id→name lookup
  // go through the kit (a head-office row reads as อารีย์).
  const branchList = useBranches()
  const drawerBranches = useMemo(
    () => branchList.map((b) => ({ id: String(b.id ?? b.branch_id), name: kitBranchName(b.id ?? b.branch_id, branchList) })),
    [branchList],
  )
  const [tagCatalog, setTagCatalog] = useState([])

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [pageTotal, setPageTotal] = useState(1)
  const [state, setState] = useState({ loading: true, err: '' })

  // drawer: { mode:'create'|'view', ticketId }
  const [drawer, setDrawer] = useState(null)

  useEffect(() => {
    cachedGet('/api/ticket/tag')
      .then((r) => setTagCatalog(normalizeTagCatalog(r)))
      .catch(() => {})
  }, [])

  // an old/unknown status value must not wedge the page
  const status = STATUS.find((s) => s.key === filters.status) || STATUS[0]
  const isNotDone = status.key === 'not_done'
  // stable primitive for the effect deps — an array identity would refetch on every render
  const tagList = Array.isArray(filters.tags) ? filters.tags : []
  const tagKey = tagList.join('|')
  const startDate = filters.range?.start || ''
  const endDate = filters.range?.end || ''
  const branchId = filters.branch

  // reset to page 1 whenever a filter changes
  useEffect(() => { setPage(1) }, [filters.status, branchId, tagKey, startDate, endDate, dueDate, patient])

  const reqId = useRef(0)
  const load = () => {
    const id = ++reqId.current
    setState({ loading: true, err: '' })
    // Xano's end_date is EXCLUSIVE — +1 day so the chosen date itself is included.
    const upTo = isNotDone ? dueDate : endDate
    // The endpoint takes ONE branch_id. branchFilterIds(8) is [8, 1] (อารีย์ also
    // covers head office), but no ticket is saved under head office, so the first
    // id is exactly what was sent before.
    const ids = branchFilterIds(branchId)
    const params = {
      mode: status.mode,
      branch_id: ids ? String(ids[0]) : undefined,
      tag: tagList.length ? tagList : undefined,   // string[] → ?tag=a&tag=b
      start_date: (isNotDone ? undefined : startDate) || undefined,
      end_date: upTo ? addDays(upTo, 1) : undefined,
      patient_id: patient?.id || undefined,
      page, per_page: perPage,
    }
    apiGet('/api/ticket', params)
      .then((r) => {
        if (id !== reqId.current) return
        setRows(asList(r))
        setTotal(Number(r?.itemsTotal ?? r?.itemsReceived ?? (Array.isArray(r) ? r.length : 0)) || 0)
        setPageTotal(Number(r?.pageTotal ?? 1) || 1)
        setState({ loading: false, err: '' })
      })
      .catch((e) => { if (id === reqId.current) setState({ loading: false, err: String(e.message || e) }) })
  }
  useEffect(load, [filters.status, branchId, tagKey, startDate, endDate, dueDate, patient, page, perPage])

  const today = bkkTodayISO()
  const canClear = !isDefault || dueDate !== today || !!patient
  function resetFilters() {
    reset(); setDueDate(bkkTodayISO()); setPatient(null)
  }

  const columns = useMemo(() => [
    {
      // urgency first (what staff actually triage on), then the exact date, then
      // when it was opened + its age
      key: 'date_followup', label: 'วันติดตาม', width: '150px',
      render: (r) => {
        const rel = tFollowupRel(r)
        const relTone = REL_TONES[rel?.tone] || REL_TONES.future
        return (
          <div className="align-top">
            {rel ? (
              <span className={`flex items-center gap-1.5 text-[12px] font-medium ${relTone.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${relTone.dot}`} />
                {rel.label}
              </span>
            ) : null}
            <span className={`block tabular-nums ${rel ? 'text-slate-600' : 'text-slate-700 font-medium'}`} title="วันติดตาม">
              {fmtDate(tDateFollowup(r))}
            </span>
            <span className="block text-[11.5px] text-slate-400 tabular-nums mt-0.5" title="วันที่สร้าง · อายุของ ticket">
              สร้าง {fmtDate(tCreatedAt(r))} · {fmtDuration(tAgeDays(r))}
            </span>
          </div>
        )
      },
    },
    {
      // details + tags — the real appointment/treatment info lives in the drawer
      key: 'details', label: 'รายละเอียด',
      render: (r) => {
        const rowTags = tTags(r)   // NOT the tags filter
        return (
          <div className="min-w-0">
            <span className="block text-slate-800 truncate" title={tDetails(r)}>{tDetails(r) || <span className="text-slate-300">—</span>}</span>
            {rowTags.length ? (
              <span className="flex flex-wrap gap-1 mt-1">
                {rowTags.slice(0, 4).map((t) => <Badge key={t}>{t}</Badge>)}
                {rowTags.length > 4 && <span className="text-[11.5px] text-slate-400 self-center">+{rowTags.length - 4}</span>}
              </span>
            ) : null}
          </div>
        )
      },
    },
    {
      // the name / HN is the link to the patient (new tab); the row opens the drawer
      key: 'patient', label: 'คนไข้', width: '190px',
      // onKeyDown stop: Enter on the link must open the patient, not the row's drawer
      // (a DataTable row turns Enter/Space from any child into a row click — kit gap)
      render: (r) => (tPatientName(r)
        ? <span onKeyDown={(e) => e.stopPropagation()}><PatientName context="followup" patient={{ id: tPatientId(r), fullname: tPatientName(r), HN: tHN(r) || undefined }} /></span>
        : <span className="text-slate-300">—</span>),
    },
    {
      // patient status (the CRM's own patient enum and its colours)
      key: 'patient_status', label: 'สถานะคนไข้', width: '150px',
      render: (r) => {
        const pStatus = tPatientStatus(r)
        if (!pStatus) return <span className="text-slate-300">—</span>
        const pTone = STATUS_TONES[statusTone(pStatus)] || STATUS_TONES.none
        return (
          <span className={`inline-flex items-center gap-1 max-w-full rounded-full px-2 py-0.5 text-[12px] ring-1 ${pTone.pill}`} title={`สถานะคนไข้: ${pStatus}`}>
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${pTone.dot}`} />
            <span className="truncate">{pStatus}</span>
          </span>
        )
      },
    },
    {
      // branch — resolved from the joined appointment
      key: 'branch', label: 'สาขา', width: '120px',
      render: (r) => <span className="block text-slate-500 truncate">{kitBranchName(tBranchId(r), branchList)}</span>,
    },
    {
      key: 'followups', label: 'ติดตาม', width: '76px', align: 'right',
      render: (r) => {
        const fu = tFollowUps(r)
        return fu.length
          ? <span className="inline-flex items-center gap-1 text-slate-500"><MessageSquare size={12} /> {fu.length}</span>
          : <span className="text-slate-300">—</span>
      },
    },
  ], [branchList])

  return (
    <Page>
      <PageHeader
        breadcrumb={[{ label: 'ติดตาม', to: '/follow-ups' }, { label: 'Ticket' }]}
        icon={TicketIcon}
        title="Ticket"
        subtitle="ติดตามงาน/เรื่องที่ต้องดำเนินการ"
        meta={!state.loading || rows.length ? `พบ ${fmtNumber(total)} รายการ` : undefined}
        actions={<Button icon={Plus} onClick={() => setDrawer({ mode: 'create' })}>สร้าง Ticket</Button>}
      >
        <Segmented
          options={STATUS.map((s) => ({ value: s.key, label: s.label }))}
          value={status.key}
          onChange={(v) => set('status', v)}
        />
      </PageHeader>

      <FilterCard onClear={resetFilters} canClear={canClear}>
        {/* patient picker replaces the free-text search (no server search param) */}
        <Field label="คนไข้" className="flex-1 min-w-[220px]">
          {patient ? (
            <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white pl-2 pr-1.5 py-1.5">
              <span className="w-5 h-5 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-[10.5px] font-semibold">{nameInitial(patient.name || '?')}</span>
              <span className="text-[13.5px] text-slate-700 truncate max-w-[200px]">{patient.name}{patient.hn ? ` · HN ${patient.hn}` : ''}</span>
              <button type="button" onClick={() => setPatient(null)} title="ล้างคนไข้" aria-label="ล้างคนไข้" className="text-slate-300 hover:text-slate-500"><X size={13} /></button>
            </div>
          ) : (
            <InlinePatientPicker onPick={setPatient} />
          )}
        </Field>

        <Field label="สาขา" className="min-w-[160px]">
          <BranchSelect value={branchId} onChange={(v) => set('branch', v)} />
        </Field>

        <Field label="Tag" className="min-w-[200px]">
          {/* Xano's `tag` param is a string[] — the proxy forwards each via tag[]=,
              so filtering by several at once is native. */}
          <TagPicker value={tagList} onChange={(v) => set('tags', v)} catalog={tagCatalog} placeholder="ทุกแท็ก" variant="filter" />
        </Field>

        {isNotDone ? (
          /* one date — every unfinished ticket due up to and including it */
          <Field label="ครบกำหนดถึงวันที่">
            <div className="flex items-center gap-2">
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-[170px]" />
              <Button
                variant={dueDate ? 'secondary' : 'soft'}
                onClick={() => setDueDate(dueDate ? '' : bkkTodayISO())}
                title={dueDate ? 'แสดงทุก ticket ที่ยังไม่เสร็จ (ล้างวันที่)' : 'กลับไปดูเฉพาะที่ครบกำหนดถึงวันนี้'}
                aria-pressed={!dueDate}>
                ทั้งหมด
              </Button>
            </div>
          </Field>
        ) : (
          <Field label="ช่วงวันติดตาม" className="min-w-[260px]">
            <DateRangePicker
              value={{ start: startDate, end: endDate }}
              onChange={(r) => set('range', r)}
              allowFuture
              presets={DONE_PRESETS}
              clearable
            />
          </Field>
        )}
      </FilterCard>

      {/* no สถานะ column for the ticket itself: the ยังไม่เสร็จ/เสร็จแล้ว view says it */}
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r, i) => `${tId(r) ?? 'row'}-${i}`}
        onRowClick={(r) => setDrawer({ mode: 'view', ticketId: tId(r) })}
        loading={state.loading}
        error={state.err ? `โหลดข้อมูลไม่สำเร็จ: ${state.err}` : null}
        onRetry={load}
        filtered={canClear}
        onClearFilters={resetFilters}
        empty={{ title: isNotDone ? 'ไม่มี ticket ที่ครบกำหนดถึงวันนี้' : 'ไม่พบ ticket' }}
        page={page}
        pageTotal={pageTotal}
        onPageChange={setPage}
        total={total}
        fixed
        minWidth={960}
      />

      {drawer && (
        <TicketDrawer
          mode={drawer.mode}
          ticketId={drawer.ticketId ?? null}
          branches={drawerBranches}
          tagCatalog={tagCatalog}
          onClose={() => setDrawer(null)}
          onSaved={load}
        />
      )}
    </Page>
  )
}

/* debounced patient picker used inline in the filter bar */
function InlinePatientPicker({ onPick }) {
  const [q, setQ] = useState('')
  const [rows, setRows] = useState([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const reqId = useRef(0)

  useEffect(() => {
    const term = q.trim()
    if (term.length < MIN_PATIENT_SEARCH) { setRows([]); setLoading(false); setOpen(false); return }
    const id = ++reqId.current
    setLoading(true)
    const t = setTimeout(() => {
      apiGet('/api/patients', { search: term, per_page: 8 })
        .then((r) => { if (id !== reqId.current) return; setRows(Array.isArray(r) ? r : (r?.items || [])); setOpen(true) })
        .catch(() => { if (id === reqId.current) setRows([]) })
        .finally(() => { if (id === reqId.current) setLoading(false) })
    }, 300)
    return () => clearTimeout(t)
  }, [q])

  function pick(p) {
    const name = [p.prefix, p.name, p.surname].filter(Boolean).join(' ').trim() || p.fullname || '—'
    onPick({ id: p.id ?? p.patient_id, name, hn: p.hn || p.HN || '' })
    setQ(''); setRows([]); setOpen(false)
  }

  const ta = useTypeahead({
    count: rows.length,
    onSelect: (i) => pick(rows[i]),
    onClose: () => setOpen(false),
  })

  return (
    <div className="relative max-w-[320px]">
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => rows.length && setOpen(true)} onKeyDown={ta.onKeyDown}
          placeholder="ค้นหาชื่อ · HN · เบอร์…" className={`${FIELD_CLASS} pl-8`} />
        {loading && <Loader2 size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
      </div>
      {open && rows.length > 0 && (
        <>
          <button className="fixed inset-0 z-10 cursor-default" onClick={() => setOpen(false)} aria-hidden tabIndex={-1} />
          <div ref={ta.listRef} className="absolute z-20 mt-1 w-[320px] max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg py-1">
            {rows.map((p, i) => {
              const name = [p.prefix, p.name, p.surname].filter(Boolean).join(' ').trim() || p.fullname || '—'
              const hn = p.hn || p.HN || ''
              return (
                <button key={p.id ?? p.patient_id} data-ti-index={i} onMouseEnter={() => ta.setActiveIndex(i)} onClick={() => pick(p)} className={`flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50 ${i === ta.activeIndex ? 'bg-slate-100' : ''}`}>
                  <span className="w-7 h-7 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-[12px] font-semibold shrink-0">{nameInitial(name)}</span>
                  <span className="min-w-0">
                    <span className="block text-[13.5px] text-slate-800 truncate">{name}</span>
                    {hn ? <span className="block text-[11.5px] text-slate-400">HN {hn}{p.phone ? ` · ${p.phone}` : ''}</span> : null}
                  </span>
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
